import { useState } from 'react';

import { Button as MuiButton, Card, Stack, Typography } from '@mui/material';

import { say, type Voice } from '@/james-playground/aa-family-claim/claim/asks';
import {
  type Facts,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import {
  BATCH,
  CARD,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  ChoiceCards,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import {
  batchValues,
  openGates,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * The batch check (spec 3): every care activity nobody has mentioned, in one
 * list. A tick says she has difficulty or needs help with it, and opens that
 * activity's card next. Anything left unticked is the family confirming she
 * manages it on her own, and the card says so in so many words before they
 * continue.
 */
const BatchCheckCard = ({
  facts,
  voice: v,
  onSubmit,
}: {
  facts: Facts;
  voice: Voice;
  onSubmit: (values: Record<string, Value>, reply: string) => void;
}) => {
  const qid = useQids();
  const [gates] = useState(() => openGates(facts));
  const [ticked, setTicked] = useState<string[]>([]);
  const toggle = (id: string) =>
    setTicked((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  const submit = () => {
    const names = gates
      .filter((g) => ticked.includes(g.id))
      .map((g) => say(g.label, v).toLowerCase());
    onSubmit(
      batchValues(
        gates.map((g) => g.id),
        ticked,
      ),
      names.length
        ? say(BATCH.reply(names.join(', ')), v)
        : say(BATCH.replyNone, v),
    );
  };

  return (
    <Card variant='padded' component='section' aria-labelledby={qid('intro')}>
      <Stack spacing={2.5}>
        <Typography id={qid('intro')} variant='body1'>
          {say(BATCH.intro, v)}
        </Typography>
        <ChoiceCards
          multi
          labelledBy={qid('intro')}
          options={gates.map((g) => ({ value: g.id, label: say(g.label, v) }))}
          selected={ticked}
          onPick={toggle}
        />
        <Typography variant='body2'>{say(BATCH.unticked, v)}</Typography>
        <MuiButton variant='contained' fullWidth onClick={submit}>
          {CARD.continue}
        </MuiButton>
      </Stack>
    </Card>
  );
};

BatchCheckCard.displayName = 'BatchCheckCard';
export default BatchCheckCard;
